import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Zap,
  Shield,
  Check,
  ArrowRight,
  Link2,
  Search,
  Settings,
  Leaf,
  DollarSign,
  Building2,
  Cpu,
  Battery,
  Building,
  Sun,
  ClipboardList,
  Gauge,
  AlertTriangle,
  ListChecks,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import googleForStartups from "@/assets/google-for-startups.svg";
import awsLogo from "@/assets/aws.svg";
import microsoftLogo from "@/assets/microsoft.svg";
import nvidiaLogo from "@/assets/nvidia.svg";
import anthropicLogo from "@/assets/anthropic.svg";
import { MobileSavingsCalculator } from "@/components/SavingsCalculator";
import { MobileCarbonCalculator } from "@/components/CarbonCalculator";

const Home = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main id="main-content" tabIndex={-1}>

      {/* 1. Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />

        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 backdrop-blur-sm border border-primary/20 text-sm text-muted-foreground animate-fade-in">
              <Zap aria-hidden="true" className="h-4 w-4 text-primary" />
              AI Revenue Recovery for Solar & Storage Portfolios
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-display font-bold leading-tight break-words">
              WoozyAI: Solar revenue recovery and storage performance optimization{" "}
              <span className="text-gradient-aurora">across every site in your portfolio.</span>
            </h1>


            <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              WoozyAI helps operators of utility-scale solar farms, distributed commercial rooftops, and
              solar-plus-storage portfolios detect underperformance, improve operational efficiency,
              prioritize action, and recover measurable financial value.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button variant="hero" size="xl" asChild>
                <Link to="/contact">
                  See WoozyAI on Your Portfolio
                  <ArrowRight aria-hidden="true" className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button variant="investor" size="xl" asChild>
                <Link to="/platform">
                  Explore the Platform
                  <ArrowRight aria-hidden="true" className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div className="space-y-4 pt-4 lg:hidden">
              <MobileSavingsCalculator />
              <MobileCarbonCalculator />
            </div>
          </div>
        </div>
      </section>

      {/* 2. Data-rich but action-poor */}
      <section className="py-20 px-6 relative">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-4xl md:text-5xl font-display font-bold">
              Solar and storage portfolios are{" "}
              <span className="text-gradient-aurora">data-rich but action-poor.</span>
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              <p>
                Data already arrives constantly - from inverters, SCADA, weather providers, asset
                registries, spreadsheets, ticketing systems, and financial models. The gap isn't data
                collection. It's connecting that data to four questions every operator needs answered:
                how much was lost, why it happened, what it's worth financially, and what to fix first.
              </p>
              <p className="font-medium text-foreground">
                Fragmented signals → delayed diagnosis → inefficient action → lost revenue.
              </p>
              <p>
                WoozyAI is built to complement the monitoring platforms, SCADA systems, and asset
                management tools operators already run - not to replace them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Workflow - 9 stages */}
      <section className="py-20 px-6 bg-card/20">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              From fragmented data to{" "}
              <span className="text-gradient-aurora">measurable recovery</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              { title: "Connect", description: "Ingest data from existing monitoring, SCADA, and financial systems." },
              { title: "Normalize", description: "Reconcile formats, units, and site metadata into one consistent model." },
              { title: "Model", description: "Build expected-performance baselines for each asset and condition." },
              { title: "Detect", description: "Identify deviations between expected and actual performance." },
              { title: "Validate", description: "Confirm findings against evidence to filter false positives." },
              { title: "Quantify", description: "Translate each finding into estimated energy and financial impact." },
              { title: "Prioritize", description: "Rank issues by recoverable value and urgency." },
              { title: "Resolve", description: "Route the recommended action to the right owner." },
              { title: "Measure", description: "Track outcomes to confirm value was actually recovered." },
            ].map((item, index) => (
              <Card key={index} className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-display font-semibold">{item.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Real product workflow */}
      <section className="py-20 px-6 relative">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold">
              Know what happened, why it matters,{" "}
              <span className="text-gradient-aurora">and what to do next</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Every finding moves through a structured Action Queue, from first detection to confirmed recovery.
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-6">
            {[
              { icon: ListChecks, title: "Action Queue", description: "A prioritized list of open issues ranked by estimated financial impact." },
              { icon: AlertTriangle, title: "Alert detail", description: "Site, asset, and condition context behind every flagged issue." },
              { icon: Gauge, title: "Actual vs. expected production", description: "A direct comparison against the modeled performance baseline." },
              { icon: Zap, title: "Detection time and alert age", description: "How long the condition has existed and how long it has been open." },
              { icon: Search, title: "Evidence and confidence", description: "The supporting data behind each finding, with a confidence level." },
              { icon: DollarSign, title: "Estimated lost kWh", description: "The estimated energy impact of the underlying condition." },
              { icon: DollarSign, title: "Financial impact", description: "Lost kWh translated into estimated financial value at risk." },
              { icon: ClipboardList, title: "Recommended action", description: "A specific, actionable next step tied to the finding." },
              { icon: Shield, title: "Ownership and status", description: "A clear owner and status as the issue moves toward resolution." },
              { icon: Check, title: "Before-and-after recovery tracking", description: "Confirmation that performance returned to expected levels after action." },
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-5 rounded-lg border border-border/50 bg-card/30">
                <div className="p-2 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                  <item.icon aria-hidden="true" className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Four value layers */}
      <section className="py-20 px-6 bg-card/20">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Value across{" "}
              <span className="text-gradient-aurora">four layers</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                icon: Sun,
                title: "Solar Revenue Recovery",
                description: "Detect underperformance, quantify lost energy and revenue, and prioritize the fixes with the greatest financial return.",
                link: "/solutions",
              },
              {
                icon: Battery,
                title: "Storage Performance Optimization",
                description: "Surface degradation, dispatch inefficiency, and availability gaps across battery assets tied to your portfolio.",
                link: "/solutions/storage",
              },
              {
                icon: Leaf,
                title: "Carbon Value Recovery",
                description: "Connect operational performance to carbon and environmental-value outcomes that depend on actual generation.",
                link: "/solutions/carbon-value",
              },
              {
                icon: Shield,
                title: "ESG Intelligence",
                description: "Provide operational evidence that supports ESG and sustainability reporting requirements, without replacing your compliance process.",
                link: "/solutions/esg",
              },
            ].map((item, index) => (
              <Card key={index} className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 flex flex-col">
                <item.icon aria-hidden="true" className="h-8 w-8 text-primary mb-4" />
                <h3 className="text-lg font-display font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground mb-4 flex-1">{item.description}</p>
                <Link to={item.link} className="text-sm font-medium text-primary inline-flex items-center gap-1 hover:underline">
                  Explore {item.title}

                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 6. One intelligence layer across environments */}
      <section className="py-20 px-6 relative">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              One intelligence layer across{" "}
              <span className="text-gradient-aurora">different solar and storage environments</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="p-8 bg-card/30 backdrop-blur-sm border-primary/30 hover:border-primary/50 transition-all duration-300">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                <Sun aria-hidden="true" className="h-4 w-4" />
                Utility-Scale Solar
              </div>
              <p className="text-muted-foreground text-sm">
                Focused on string- and inverter-level underperformance, large-scale yield-gap analysis,
                and prioritizing maintenance across sprawling sites.
              </p>
            </Card>

            <Card className="p-8 bg-card/30 backdrop-blur-sm border-secondary/30 hover:border-secondary/50 transition-all duration-300">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 text-secondary text-sm font-medium mb-4">
                <Building aria-hidden="true" className="h-4 w-4" />
                Commercial Rooftop Portfolios
              </div>
              <p className="text-muted-foreground text-sm">
                Focused on managing performance across many smaller, distributed sites where issues are
                easy to miss without portfolio-wide visibility.
              </p>
            </Card>

            <Card className="p-8 bg-card/30 backdrop-blur-sm border-aurora-blue/30 hover:border-aurora-blue/50 transition-all duration-300">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aurora-blue/10 text-aurora-blue text-sm font-medium mb-4">
                <Battery aria-hidden="true" className="h-4 w-4" />
                Solar + Storage
              </div>
              <p className="text-muted-foreground text-sm">
                Focused on the combined dynamics of generation and dispatch, where storage degradation
                and scheduling inefficiency directly affect revenue.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* 7. Measured value, not more alerts */}
      <section className="py-20 px-6 bg-card/20">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold">
              Measured value,{" "}
              <span className="text-gradient-aurora">not more alerts</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              WoozyAI is designed to report on outcomes, not just generate additional notifications.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { title: "Detected value", description: "The estimated financial impact of an issue at the moment it is identified." },
              { title: "Recoverable value", description: "The portion of detected value that is realistically actionable given site conditions and constraints." },
              { title: "Recovered value", description: "The value confirmed after an action has been taken and performance has been verified." },
              { title: "Time to detection", description: "How quickly a condition is identified after it begins affecting performance." },
              { title: "Time to resolution", description: "How long it takes from detection to confirmed action." },
            ].map((item, index) => (
              <Card key={index} className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
                <h3 className="font-display font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </Card>
            ))}
          </div>

          <p className="text-center text-sm text-muted-foreground max-w-2xl mx-auto mt-10">
            Customer-verified figures for detected, recoverable, and recovered value are shared during a
            portfolio review, based on your own data.
          </p>
        </div>
      </section>

      {/* 8. Works above existing operational stack */}
      <section className="py-20 px-6 relative">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Works above your{" "}
              <span className="text-gradient-aurora">existing operational stack</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              WoozyAI is designed to sit on top of the systems you already run, not to replace them.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {[
              { name: "Inverter platforms", status: "Standard connector" },
              { name: "SCADA", status: "Standard connector" },
              { name: "Metering", status: "Standard connector" },
              { name: "BMS / EMS", status: "In validation" },
              { name: "Weather and irradiance", status: "Standard connector" },
              { name: "Satellite / Earth observation", status: "In validation" },
              { name: "Asset registries", status: "Standard connector" },
              { name: "Historical exports", status: "Standard connector" },
              { name: "Ticketing / CMMS", status: "In validation" },
              { name: "Tariff / PPA and financial data", status: "Planned" },
            ].map((item, index) => (
              <div key={index} className="flex items-center justify-between gap-4 p-4 rounded-lg border border-border/50 bg-card/30">
                <span className="text-sm font-medium">{item.name}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap border border-border/50 rounded-full px-3 py-1">
                  {item.status}
                </span>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button variant="outline" size="lg" asChild>
              <Link to="/integrations">
                View Integrations
                <ArrowRight aria-hidden="true" className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Startup Ecosystem Trust Section */}
      <section className="pb-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <Card className="p-8 md:p-10 bg-card/40 backdrop-blur-sm border-border/50">
            <div className="space-y-8">
              <div className="space-y-3 text-center md:text-left">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                  Backed by
                </p>
                <h2 className="text-2xl md:text-3xl font-display font-semibold">
                  Selected for leading global startup programs
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  WoozyAI has received substantial non-dilutive cloud, AI, infrastructure, tooling, and
                  technical support from several of the world's leading technology ecosystems, including
                  access to engineering resources, product experts, technical office hours, and founder
                  programs.
                </p>
              </div>

              <ul className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
                {[
                  { src: googleForStartups, alt: "Google for Startups", label: "Google for Startups" },
                  { src: awsLogo, alt: "Amazon Web Services", label: "Amazon / AWS Activate" },
                  { src: microsoftLogo, alt: "Microsoft", label: "Microsoft for Startups" },
                  { src: nvidiaLogo, alt: "NVIDIA", label: "NVIDIA Inception" },
                  { src: anthropicLogo, alt: "Anthropic", label: "Anthropic for Startups" },
                ].map((program) => (
                  <li key={program.label} className="space-y-3 text-center">
                    <div className="flex h-32 items-center justify-center rounded-lg border border-border/50 bg-card/60 p-2 transition-colors hover:border-primary/30">
                      <img
                        src={program.src}
                        alt={program.alt}
                        loading="lazy"
                        decoding="async"
                        className="max-h-full w-auto max-w-full object-contain"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground leading-snug">{program.label}</p>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      </section>

      {/* 9. Final CTA */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-aurora opacity-5" />
        <div className="absolute inset-0 grid-pattern opacity-20" />

        <div className="container mx-auto relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-display font-bold">
              Find the first{" "}
              <span className="text-gradient-aurora">measurable recovery opportunities</span>{" "}
              in your portfolio
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Select a representative portfolio, review the available data, and define the first
              operational, storage, financial, or environmental-value use cases.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button variant="hero" size="xl" asChild>
                <Link to="/contact">
                  Book a Portfolio Review
                  <ArrowRight aria-hidden="true" className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button variant="outline" size="xl" asChild>
                <Link to="/contact">
                  Contact WoozyAI
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};

export default Home;
